import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { collectEvent, USER_CLICK_TRACK_ID, UserAction, userActionTypeEnum, } from '@thoughtspot/metrics';
import { invalidateSessionInfoCallCache } from '@thoughtspot/mfe-utilities';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { Colors, Typography, } from '@thoughtspot/radiant-react/widgets/typography';
import { getPreferredLocale, getSessionTimeZone, } from '@thoughtspot/session-service';
import React, { useEffect, useState } from 'react';
import { BannerLevel, } from "../../../services/generated/graphql-types";
import { useDeleteBannerConfig, useGetBannerConfig, useSetBannerConfig, } from "../../../services/session/session-service";
import { BannerStatus, buildMixPanelProperties, formatDateTimeToUtcMillis, getAnnouncementTypeFromApi, getApiTypeFromAnnouncement, getBannerStatus, getBannerType, parseApiDateTime, StartTimeType, } from './banner-utils';
import { ConfigAnnouncement } from './config-announcement';
import styles from './config-announcement.module.scss';
export const ConfigAnnouncementContainer = ({ bannerLevel, onShowToast, }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const { t } = useTranslation();
    const timezone = getSessionTimeZone();
    const locale = getPreferredLocale();
    const { data: bannerConfig, refetch: refetchBanner } = useGetBannerConfig(bannerLevel);
    const { setBannerConfig, loading: isSaving } = useSetBannerConfig();
    const { deleteBannerConfig, loading: isDeleting } = useDeleteBannerConfig();
    const [bannerFormData, setBannerFormData] = useState(null);
    useEffect(() => {
        if (bannerConfig) {
            const parsedStartTime = parseApiDateTime(bannerConfig.startTime, timezone);
            const parsedEndTime = parseApiDateTime(bannerConfig.endTime, timezone);
            const bannerData = {
                type: getAnnouncementTypeFromApi(bannerConfig.type),
                caption: bannerConfig?.content || '',
                linkLabel: bannerConfig.linkText || '',
                linkUrl: bannerConfig.linkUrl || '',
                startTimeType: bannerConfig.startTime
                    ? StartTimeType.CUSTOM
                    : StartTimeType.FROM_NOW,
                customStartDate: bannerConfig.startTime
                    ? parsedStartTime.date
                    : null,
                customStartTime: bannerConfig.startTime
                    ? {
                        hour: parsedStartTime.hour,
                        minute: parsedStartTime.minute,
                    }
                    : undefined,
                endDate: bannerConfig.endTime ? parsedEndTime.date : null,
                endTime: bannerConfig.endTime
                    ? {
                        hour: parsedEndTime.hour,
                        minute: parsedEndTime.minute,
                    }
                    : undefined,
            };
            setBannerFormData(bannerData);
        }
        else {
            setBannerFormData(null);
        }
    }, [bannerConfig]);
    const handleEditClick = () => {
        setIsModalOpen(true);
        setIsPreviewOpen(false);
        collectEvent(new UserAction(userActionTypeEnum.CONFIGURE_ANNOUNCEMENT_EDIT), {
            orgTab: bannerLevel,
        });
    };
    const handleModalDismiss = () => {
        setIsModalOpen(false);
        collectEvent(new UserAction(userActionTypeEnum.CLICK), {
            trackId: USER_CLICK_TRACK_ID.CANCEL_CONFIGURE_ANNOUNCEMENT,
            orgTab: bannerLevel,
        });
    };
    const handlePreviewClick = () => {
        if (!bannerConfig)
            return;
        setIsPreviewOpen(true);
        collectEvent(new UserAction(userActionTypeEnum.PREVIEW_CUSTOM_ALERT), buildMixPanelProperties(bannerConfig, bannerLevel, false));
    };
    const handleSave = async (updatedFormData) => {
        const bannerData = {
            type: getApiTypeFromAnnouncement(updatedFormData.type),
            content: updatedFormData.caption,
            linkText: updatedFormData.linkLabel,
            linkUrl: updatedFormData.linkUrl,
            startTime: updatedFormData.startTimeType === StartTimeType.FROM_NOW
                ? new Date().getTime()
                : formatDateTimeToUtcMillis(updatedFormData.customStartDate, updatedFormData.customStartTime.hour, updatedFormData.customStartTime.minute, timezone),
            endTime: formatDateTimeToUtcMillis(updatedFormData.endDate, updatedFormData.endTime.hour, updatedFormData.endTime.minute, timezone),
        };
        collectEvent(new UserAction(userActionTypeEnum.CONFIGURE_ANNOUNCEMENT_DONE), buildMixPanelProperties(bannerData, bannerLevel, true));
        await setBannerConfig(bannerLevel, bannerData);
        invalidateSessionInfoCallCache();
        refetchBanner();
        setIsModalOpen(false);
        onShowToast(t('admin.home.announcement.toast.messageSaved'));
    };
    const handleDelete = async () => {
        collectEvent(new UserAction(userActionTypeEnum.CLICK), {
            trackId: USER_CLICK_TRACK_ID.DELETE_CONFIGURE_ANNOUNCEMENT,
            orgTab: bannerLevel,
        });
        await deleteBannerConfig(bannerLevel);
        invalidateSessionInfoCallCache();
        refetchBanner();
        setIsModalOpen(false);
        onShowToast(t('admin.home.announcement.toast.messageDeleted'));
    };
    const getStatusText = () => {
        const status = getBannerStatus(bannerConfig);
        const formattingOptions = {
            hour12: false,
        };
        switch (status) {
            case BannerStatus.SCHEDULED:
                return `${t('admin.home.announcement.config.status.scheduledFor')} ${bannerFormData?.customStartDate?.toLocaleString(locale, formattingOptions)}`;
            case BannerStatus.ACTIVE:
                return `${t('admin.home.announcement.config.status.activeTill')} ${bannerFormData?.endDate?.toLocaleString(locale, formattingOptions)}`;
            case BannerStatus.INACTIVE:
                return t('admin.home.announcement.config.status.inactive');
            default:
                return t('admin.home.announcement.config.status.inactive');
        }
    };
    return (React.createElement("div", { className: styles.configAnnouncementContainer },
        React.createElement(Vertical, null,
            React.createElement("div", null,
                React.createElement(Horizontal, { className: styles.header },
                    React.createElement("div", null,
                        React.createElement(Typography, { role: "heading", "aria-level": 1, variant: "p-large-bold", className: styles.title }, t('admin.home.announcement.config.title')),
                        React.createElement(Typography, { variant: "p", color: Colors.grayLight, className: styles.subtitle }, bannerLevel === BannerLevel.Cluster
                            ? t('admin.home.announcement.config.description')
                            : t('admin.home.announcement.config.descriptionForOrg'))),
                    React.createElement("div", { className: styles.headerControls },
                        React.createElement(Button, { text: t('EDIT'), type: "secondary", onClick: handleEditClick, "data-testid": "custom-banner-edit-button" })))),
            React.createElement(Horizontal, null,
                React.createElement(Horizontal, { className: styles.statusInfo },
                    React.createElement(Typography, { variant: "p", color: Colors.base, className: styles.statusText }, t('admin.home.announcement.config.status.text')),
                    React.createElement(Typography, { variant: "p", color: Colors.base, className: styles.statusDescription }, getStatusText())),
                !isPreviewOpen &&
                    getBannerStatus(bannerConfig) ===
                        BannerStatus.SCHEDULED && (React.createElement(Button, { text: t('admin.home.announcement.config.status.preview'), className: styles.previewButton, type: "tertiary", onClick: handlePreviewClick, "data-testid": "modular-customize-button-testid" })))),
        isModalOpen && (React.createElement(ConfigAnnouncement, { onDismiss: handleModalDismiss, onSave: handleSave, isSaving: isSaving, onDelete: handleDelete, isDeleting: isDeleting, initialData: bannerFormData || undefined, isCreateMode: bannerFormData === null, timezone: timezone, bannerLevel: bannerLevel })),
        bannerConfig?.content && isPreviewOpen && (React.createElement(GlobalAlert.Banner, { isVisible: true, message: bannerConfig?.content, type: getBannerType(getAnnouncementTypeFromApi(bannerConfig.type)), showCloseButton: true, onClose: () => {
                setIsPreviewOpen(false);
            }, global: true, closeOnUnmount: true, link: bannerConfig.linkText && bannerConfig.linkUrl
                ? {
                    label: bannerConfig.linkText,
                    link: bannerConfig.linkUrl,
                    openLinkInNewTab: true,
                }
                : undefined, "data-testid": "custom-banner-preview" }))));
};
//# sourceMappingURL=config-announcement.container.js.map